import {
  dropUnsetTokenResponseFields,
  oauthAuth,
} from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

const gatewayUrl = (slug: string | undefined) =>
  `https://api.arcade.dev/mcp/${encodeURIComponent(slug || "your-gateway-slug")}`;

export const arcade = defineConnector({
  identifier: "arcade",
  name: "Arcade",
  tagLine: "Access approved tools through an MCP gateway",
  keywords: "mcp gateway tools governance authentication",
  logo,
  tags: ["productivity"],
  protocol: "http",
  defaultUrlPattern: ({ gatewaySlug }) =>
    `${gatewayUrl(gatewaySlug).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}/?(?:\\?.*)?$`,
  auth: oauthAuth,
  oauth: {
    grantType: () => "authorization_code",
    clientCredentialsFrom: "dcr",
    discoverFrom: ({ gatewaySlug }) => gatewayUrl(gatewaySlug),
    authorizationEndpoint: (cfg) =>
      (cfg as Record<string, string>).authorizeUrl ?? "",
    tokenEndpoint: (cfg) => (cfg as Record<string, string>).tokenUrl ?? "",
    issuer: (cfg) => (cfg as Record<string, string>).issuer ?? "",
    tokenEndpointAuthMethod: (_cfg, { hasSecret }) =>
      hasSecret ? "client_secret_post" : "none",
    pkce: () => true,
    resource: (cfg) =>
      (cfg as Record<string, string>).resource ?? gatewayUrl(cfg.gatewaySlug),
    scopes: (cfg) =>
      ((cfg as Record<string, string>).discoveredScopes ?? "")
        .split(/\s+/)
        .filter(Boolean),
    normalizeTokenResponse: dropUnsetTokenResponseFields,
  },
  steps: [
    {
      title: "Choose your Arcade gateway",
      items: [
        {
          kind: "markdown",
          content:
            "In [Arcade](https://docs.arcade.dev/en/operate/governance/mcp-gateways/create-via-dashboard), create or choose an MCP gateway with your approved tools. Set authentication to **Arcade Auth** for Arcade project members or a **User Source** for users signing in through your identity provider. This connector uses browser-based OAuth, not Arcade Headers or an administrator API key.",
        },
        {
          kind: "input",
          label: "Gateway slug",
          required: true,
          placeholder: "approved-tools",
          helperText:
            "The part after /mcp/ in your gateway URL, https://api.arcade.dev/mcp/your-gateway-slug. Only this gateway receives the credential; the Engine REST API and other gateways are excluded.",
          target: { kind: "configuration", field: "gatewaySlug" },
        },
      ],
    },
    {
      title: "Sign in to Arcade",
      items: [
        {
          kind: "markdown",
          content:
            "Sign in as a user permitted to access this gateway. With Arcade Auth, you must be a member of its Arcade project. With a User Source, sign in through the identity provider configured on the gateway. Leave the client fields blank for automatic OAuth client registration, or enter your own registered OAuth client and configure its redirect URI below.",
        },
        {
          kind: "input",
          label: "Client ID",
          helperText: "Optional. Leave blank to register automatically.",
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          helperText:
            "Only needed if your registered OAuth client has a secret.",
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
        {
          kind: "copyable",
          label: "Redirect URI",
          value: "{API_URL}/connected-apps/oauth/arcade/callback",
        },
        {
          kind: "oauth",
          dependsOn: [{ kind: "configuration", field: "gatewaySlug" }],
        },
      ],
    },
  ],
  publicEnvVars: ({ gatewaySlug }) => ({
    ARCADE_MCP_URL: gatewayUrl(gatewaySlug),
  }),
  usageHint: ({ gatewaySlug }) =>
    `Connect to the Arcade MCP gateway at ${gatewayUrl(gatewaySlug)} over Streamable HTTP using JSON-RPC 2.0. Load the mcp-client skill for initialization, tool discovery, and tool calls; OAuth authentication is injected automatically. Arcade controls the gateway’s allowed tools. Tool-provider consent is separate from gateway sign-in. Calls use the Arcade identity that authorized this connector, not automatically the identity of each 3B user. This connector does not authenticate Engine REST API requests.`,
  connectionTest: {
    protocol: "http",
    request: ({ gatewaySlug }) => ({
      url: gatewayUrl(gatewaySlug),
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json, text/event-stream",
      },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: 1,
        method: "initialize",
        params: {
          protocolVersion: "2025-06-18",
          capabilities: {},
          clientInfo: { name: "3B", version: "1.0" },
        },
      }),
      expectedStatus: 200,
      expectedBodyContains: ['"result"', '"protocolVersion"', '"serverInfo"'],
    }),
  },
});
